import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface IRef {
  _id: string | number;
  name: string;
}

type RequestType = 'group' | 'course' | 'activity';

interface IJoinRequest {
  _id: string | number;
  type: RequestType;
  status: 'pending' | 'approved' | 'denied';
  member: IRef;
  target: IRef;
  processedBy?: IRef | null;
  requestedAt: string;
  processedAt?: string | null;
}

interface IPendingCount {
  total: number;
  group: number;
  course: number;
  activity: number;
}

type StatusFilter = 'pending' | 'approved' | 'denied';

// Member-side group requesting is retired; only Courses and Activities are shown.
// A legacy ?tab=group deep-link falls back to the default (course).
const TYPE_TABS: RequestType[] = ['course', 'activity'];

const ApprovalsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [requests, setRequests] = useState<IJoinRequest[]>([]);
  const [counts, setCounts] = useState<IPendingCount>({ total: 0, group: 0, course: 0, activity: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [processingId, setProcessingId] = useState<string | number | null>(null);

  // Matches the backend /api/join-requests permission and the sidebar gate.
  const canApprove = hasPermission('members:write');

  // Active type tab — read from the ?tab= query param.
  const tabParam = searchParams.get('tab');
  const typeTab: RequestType = TYPE_TABS.includes(tabParam as RequestType)
    ? (tabParam as RequestType)
    : 'course';

  const setTypeTab = useCallback((type: RequestType) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', type);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await api.joinRequests.pendingCount();
      setCounts(data as IPendingCount);
    } catch {
      // Non-critical — badges stay at their last value.
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.joinRequests.list({ status: statusFilter, type: typeTab });
      setRequests(data as IJoinRequest[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeTab]);

  // On first mount, default the tab to the first type with pending requests.
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialized) return;
    if (tabParam && TYPE_TABS.includes(tabParam as RequestType)) {
      setInitialized(true);
      return;
    }
    // Wait until counts have been fetched at least once before defaulting.
    const firstWithPending = TYPE_TABS.find((type) => counts[type] > 0);
    if (firstWithPending && firstWithPending !== 'course') {
      setTypeTab(firstWithPending);
    }
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (id: string | number) => {
    if (!canApprove) return;
    setProcessingId(id);
    try {
      await api.joinRequests.approve(Number(id));
      addToast(t('approvals.approveSuccess'), 'success');
      fetchRequests();
      fetchCounts();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async (id: string | number) => {
    if (!canApprove) return;
    setProcessingId(id);
    try {
      await api.joinRequests.deny(Number(id));
      addToast(t('approvals.denySuccess'), 'success');
      fetchRequests();
      fetchCounts();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString?: string | null) =>
    dateString ? new Date(dateString).toLocaleString() : '-';

  const typeTabStyle = (active: boolean, position: 'left' | 'middle' | 'right'): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 1rem',
    border: '1px solid var(--border-color)',
    borderLeft: position === 'left' ? '1px solid var(--border-color)' : 'none',
    borderRadius:
      position === 'left' ? '4px 0 0 4px' : position === 'right' ? '0 4px 4px 0' : '0',
    backgroundColor: active ? 'var(--orange-primary)' : 'var(--bg-light)',
    color: active ? 'white' : 'var(--text-primary)',
    cursor: 'pointer',
    boxShadow: 'none',
  });

  const statusTabStyle = (
    active: boolean,
    position: 'left' | 'middle' | 'right',
    activeColor: string,
  ): React.CSSProperties => ({
    display: 'inline-flex',
    padding: '0.5rem 1rem',
    border: '1px solid var(--border-color)',
    borderLeft: position === 'left' ? '1px solid var(--border-color)' : 'none',
    borderRadius:
      position === 'left' ? '4px 0 0 4px' : position === 'right' ? '0 4px 4px 0' : '0',
    backgroundColor: active ? activeColor : 'var(--bg-light)',
    color: active ? 'white' : 'var(--text-primary)',
    cursor: 'pointer',
    boxShadow: 'none',
  });

  const typeTabLabel: Record<RequestType, string> = {
    group: t('approvals.tabGroups'),
    course: t('approvals.tabCourses'),
    activity: t('approvals.tabActivities'),
  };

  return (
    <div className="admin-list-page">
      <h2>{t('approvals.title')}</h2>

      {/* Type tabs */}
      <div className="type-toggle" style={{ display: 'flex', marginBottom: '1rem' }}>
        {TYPE_TABS.map((type, idx) => {
          const position = idx === 0 ? 'left' : idx === TYPE_TABS.length - 1 ? 'right' : 'middle';
          return (
            <button
              key={type}
              onClick={() => setTypeTab(type)}
              style={typeTabStyle(typeTab === type, position)}
            >
              {typeTabLabel[type]}
              {counts[type] > 0 && <span className="nav-badge">{counts[type]}</span>}
            </button>
          );
        })}
      </div>

      {/* Status filter */}
      <div className="status-toggle" style={{ display: 'flex', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setStatusFilter('pending')}
          style={statusTabStyle(statusFilter === 'pending', 'left', 'var(--orange-primary)')}
        >
          {t('approvals.pending')}
        </button>
        <button
          onClick={() => setStatusFilter('approved')}
          style={statusTabStyle(statusFilter === 'approved', 'middle', '#27ae60')}
        >
          {t('approvals.approved')}
        </button>
        <button
          onClick={() => setStatusFilter('denied')}
          style={statusTabStyle(statusFilter === 'denied', 'right', '#e74c3c')}
        >
          {t('approvals.denied')}
        </button>
      </div>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      {loading ? (
        <p>{t('approvals.loading')}</p>
      ) : requests.length === 0 ? (
        <p>{t('approvals.noApprovals')}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('approvals.member')}</th>
              <th>{t('approvals.target')}</th>
              <th>{t('approvals.requestedAt')}</th>
              {statusFilter !== 'pending' && (
                <>
                  <th>{t('approvals.processedAt')}</th>
                  <th>{t('approvals.processedBy')}</th>
                </>
              )}
              {statusFilter === 'pending' && canApprove && <th>{t('common.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request._id}>
                <td>{request.member?.name || t('common.noResults')}</td>
                <td>{request.target?.name || t('common.noResults')}</td>
                <td>{formatDate(request.requestedAt)}</td>
                {statusFilter !== 'pending' && (
                  <>
                    <td>{formatDate(request.processedAt)}</td>
                    <td>{request.processedBy?.name || '-'}</td>
                  </>
                )}
                {statusFilter === 'pending' && canApprove && (
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="action-btn approve-btn"
                        onClick={() => handleApprove(request._id)}
                        disabled={processingId === request._id}
                        style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#27ae60',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          opacity: processingId === request._id ? 0.7 : 1,
                        }}
                      >
                        {t('approvals.approve')}
                      </button>
                      <button
                        className="action-btn deny-btn"
                        onClick={() => handleDeny(request._id)}
                        disabled={processingId === request._id}
                        style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          opacity: processingId === request._id ? 0.7 : 1,
                        }}
                      >
                        {t('approvals.deny')}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ApprovalsPage;
